import FieldValidator from "./field-validator"
import { arrayEqualsDeep } from "@/utils"

/**
 * Validation mixins
 *
 * usage template no model
 *    <input type="email" @focus="email_onFocus" @input="email_onInput">
 *
 * usage template with model
 *    <input type="email" v-model="email" @focus="email_onFocus" >
 *
 * usage cmp:
 *    mixins: [validator],
 *    validations: {  // only flat structure
 *      email: [required, size(2)] // key must correspond with data
 *    }
 *
 * validator result:
 *    computed: {
 *      email_errorsIfTouched() {},
 *      email_errorsIfDirty() {},
 *      email_errors() {},
 *      email_valid() {},
 *      email_dirty() {},
 *      group_errors() {},
 *      group_valid() {},
 *      group_dirty() {}
 *    },
 *    data() {  // only if field does not exist in data
 *      return {
 *        email: undefined,
 *        email_touched: false, // set by focus handler
 *        email_initValue: undefined // initial value, setted at creation or on reset
 *      }
 *    }
 *    methods: {
 *      email_onFocus() {},
 *      email_onInput() {},
 *      email_reset(value) {}
 *      group_reset() {}
 *    }
 */
export default {
  beforeCreate() {
    if (!this.$options.validations) {
      throw new Error(
        "Validator Error: No validations found, you must specify validation or remove validation mixin."
      )
    }
    const validations = this.$options.validations
    const fieldValidators = Object.entries(validations).map(([key, rules]) => {
      return new FieldValidator(key, rules)
    })
    this.$options.computed = {
      ...generateComputedForValidators(fieldValidators, this),
      ...generateComputedForGroup(fieldValidators, this),
      ...this.$options.computed
    }
    this.$options.methods = {
      ...generateMethodsForValidators(fieldValidators, this),
      ...generateMethodsForGroup(fieldValidators, this),
      ...this.$options.methods
    }
  },
  data() {
    const keys = Object.keys(this.$options.validations)
    return keys.reduce((data, key) => {
      data[key] = undefined
      data[`${key}_touched`] = false
      data[`${key}_initValue`] = undefined
      return data
    }, {})
  },
  created() {
    Object.keys(this.$options.validations).forEach(key => {
      this[`${key}_initValue`] = this[key]
    })
  }
}

function generateComputedForValidators(validators, cmp) {
  return validators.reduce((computed, val) => {
    computed[`${val.key}_errors`] = () => {
      return val.validate(cmp[val.key], cmp)
    }
    computed[`${val.key}_valid`] = () => {
      return cmp[`${val.key}_errors`].length === 0
    }
    computed[`${val.key}_errorsIfTouched`] = () => {
      if (cmp[`${val.key}_touched`] || cmp[`${val.key}_dirty`]) {
        return cmp[`${val.key}_errors`]
      }
      return []
    }
    computed[`${val.key}_dirty`] = () => {
      const currentVal = cmp[val.key]
      const initalVal = cmp[`${val.key}_initValue`]
      if (Array.isArray(currentVal) && Array.isArray(initalVal)) {
        return !arrayEqualsDeep(currentVal, initalVal)
      }
      return currentVal !== initalVal
    }
    computed[`${val.key}_errorsIfDirty`] = () => {
      if (cmp[`${val.key}_dirty`]) {
        return cmp[`${val.key}_errors`]
      }
      return []
    }
    return computed
  }, {})
}

function generateComputedForGroup(validators, cmp) {
  return {
    group_errors: () => {
      return validators.reduce((errors, val) => {
        errors.push(...cmp[`${val.key}_errors`])
        return errors
      }, [])
    },
    group_valid: () => {
      return cmp.group_errors.length === 0
    },
    group_dirty: () => {
      return validators.reduce((form_dirty, val) => {
        return form_dirty || cmp[`${val.key}_dirty`]
      }, false)
    }
  }
}

function generateMethodsForValidators(validators, cmp) {
  return validators.reduce((methods, val) => {
    methods[`${val.key}_onFocus`] = () => {
      if (cmp[`${val.key}_touched`] !== true) {
        cmp[`${val.key}_touched`] = true
      }
    }
    methods[`${val.key}_onInput`] = $event => {
      if ($event instanceof Event) {
        cmp[val.key] = $event.target.value
      } else {
        cmp[val.key] = $event
      }
    }
    methods[`${val.key}_reset`] = value => {
      cmp[val.key] = value
      cmp[`${val.key}_initValue`] = value
      cmp[`${val.key}_touched`] = false
    }
    return methods
  }, {})
}

function generateMethodsForGroup(validators, cmp) {
  return {
    group_reset: () => {
      return validators.forEach(val => {
        const initValue = cmp[`${val.key}_initValue`]
        cmp[`${val.key}_reset`](initValue)
      })
    }
  }
}
